import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../../../../core/http.service';

/** HR-08: one attendance-processing run of the company (nightly or manual). */
export interface AttendanceRun {
  id: number;
  fromDate: string;
  toDate: string;
  trigger: 'Nightly' | 'Manual';
  triggeredById?: string | null;
  startedAt: string;
  finishedAt?: string | null;
  employees: number;
  days: number;
  processed: number;
  absences: number;
  deductions: number;
  noStandard: number;
  error?: string | null;
}

export interface AttendanceRunsPage {
  data: AttendanceRun[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

/** att_review_01: one finding about the attendance rule set (GET Attendance/rules/health). */
export interface RuleHealthItem {
  code: string;
  severity: 'info' | 'warning' | 'error';
  message: string;
  /** A bare page code (030800010006 absence rules, 030800010005 delay / early-leave rules). */
  link?: string | null;
}

export interface ProcessAttendanceRequest {
  fromDate: string;
  toDate: string;
  employeeIds?: number[];
}

@Injectable({ providedIn: 'root' })
export class AttendanceProcessingService {
  constructor(private http: HttpService) {}

  /** Processes (or re-processes) the range for the caller's company; needs Update on the attendance screen. */
  process(body: ProcessAttendanceRequest): Observable<{ message: string; data: AttendanceRun }> {
    return this.http.postData('Attendance/process', body);
  }

  /** att_review_01: gaps and missing rules the processing would hit; an empty list means the rules are healthy. */
  rulesHealth(): Observable<{ items: RuleHealthItem[] }> {
    return this.http.getData('Attendance/rules/health');
  }

  runs(page = 1, pageSize = 10): Observable<AttendanceRunsPage> {
    return this.http.getData(`Attendance/runs?page=${page}&pageSize=${pageSize}`);
  }
}

/******************************************* HR-08-T02/T03: ingestion, corrections, monthly close ******************************** */

export interface PunchImportReport {
  received: number;
  imported: number;
  duplicates: number;
  unknown: number;
  locked: number;
  from?: string | null;
  to?: string | null;
  errors?: { row: number; message: string }[];
}

export interface AttendanceDevice {
  id: number;
  descriptionAr: string;
  descriptionEn: string;
  ipAddress?: string | null;
  portNo?: string | null;
  /** att_review_04: the stored admin password is never returned, only whether one is set. */
  hasAdminPass?: boolean;
  active: boolean;
  autoTransTime: number;
  hasKey: boolean;
  deviceKeyHint?: string | null;
  deviceKeyCreatedAt?: string | null;
  pullAdapter?: string | null;
  lastPullAt?: string | null;
  lastPullError?: string | null;
  lastPunchReceivedAt?: string | null;
  /** HR-08: ZKTeco ADMS (iclock) serial number; null: ADMS push off. */
  serialNumber?: string | null;
  lastAdmsContactAt?: string | null;
  /** ATT-01: the on-premises agent that pulls the device (null: none), and the health it last reported. */
  agentId?: number | null;
  agentSyncUsers?: boolean;
  agentSyncClock?: boolean;
  agentReachable?: boolean | null;
  agentStatusAt?: string | null;
  agentLastError?: string | null;
  /** ATT-02: how the device sends punches. */
  mode?: DeviceMode;
  admsKeyHint?: string | null;
  firmware?: string | null;
  /** Punches the device sent for attendance numbers no employee has yet; stored when an employee gets the number. */
  unmatchedPunches?: number;
  /** att_review_03: comma-separated IPs / CIDR ranges a legacy ADMS device may call from; empty: any address. */
  allowedPushCidrs?: string | null;
}

/** ATT-02: one mode at a time. LegacyPush: an ADMS device registered by serial only (before ATT-02). */
export type DeviceMode = 'None' | 'AgentPull' | 'DevicePush' | 'CloudPull' | 'LegacyPush';

export interface DevicePushSetup {
  key: string;
  /** The server path to type in the device's cloud server setting: /iclock/{key}. */
  path: string;
  port: number;
}

export interface AdmsRequest {
  at: string;
  method: string;
  path: string;
  status: number;
  summary?: string | null;
  /** att_review_03: the caller's address (refused calls are logged too: status 404 with the reason in summary). */
  ip?: string | null;
}

export interface DeviceCommandRow {
  id: number;
  type: string;
  status: 'Pending' | 'Sent' | 'Done' | 'Failed' | 'Expired';
  result?: string | null;
  parts: number;
  doneParts: number;
  failedParts: number;
  createdAt: string;
  completedAt?: string | null;
}

export interface IssuedDeviceKey {
  key: string;
  hint: string;
  header: string;
  endpoint: string;
}

export type CorrectionStatus = 'Pending' | 'Approved' | 'Rejected';

export interface AttendanceCorrection {
  id: number;
  employeeId: number;
  employeeNameAr?: string;
  employeeNameEn?: string;
  date: string;
  checkIn?: string | null;
  checkOut?: string | null;
  /** att_review_09: the day's recorded times before the correction (shown next to the proposed ones when the API sends them). */
  originalCheckIn?: string | null;
  originalCheckOut?: string | null;
  excuse: boolean;
  reason: string;
  attachmentPath?: string | null;
  status: CorrectionStatus;
  requestedById?: string | null;
  requestedAt: string;
  decidedById?: string | null;
  decidedAt?: string | null;
  decisionNote?: string | null;
  /** MOB-10: set when the employee asked from the app (1 forgot, 2 device failure, 3 wrong direction, 4 remote approved, 5 other). */
  reasonCode?: number | null;
  /** MOB-10: when the employee's manager approved it (an app request waits for the manager before HR, unless HR only decides). */
  managerApprovedAt?: string | null;
  payrollAdjustmentId?: number | null;
}

export interface MonthCloseChecks {
  monthEnded: boolean;
  unprocessedDays: number;
  pendingAbsences: number;
  pendingDeductions: number;
  pendingCorrections: number;
  canClose: boolean;
}

export interface MonthCloseStatus {
  year: number;
  month: number;
  closed: boolean;
  closedAt?: string | null;
  closedById?: string | null;
  reopenedAt?: string | null;
  reopenReason?: string | null;
  checks: MonthCloseChecks;
  canClose: boolean;
}


/** att_review_10: one punch of the employee day (source, evidence, coordinates of a phone punch). */
export interface DayPunch {
  id: number; at: string; source: string; active: boolean; correctionId?: number | null; machineName?: string | null; method?: string | null;
  latitude?: number | null; longitude?: number | null; mapUrl?: string | null; flags?: string[];
}
export interface DayDecisionRow { id: number; status: string; decidedById?: string | null; decidedAt?: string | null; deductionPercentage?: number | null; noRuleMatched?: boolean; type?: string; violationMinutes?: number }
/** att_review_10: GET api/Attendance/day (times HH:mm:ss or local ISO without Z). */
export interface DayView {
  employeeId: number; employeeNameAr?: string | null; employeeNameEn?: string | null; date: string; monthClosed: boolean;
  planned?: { shiftId?: number | null; shiftAr?: string | null; shiftEn?: string | null; offKind?: string | null; isRamadan?: boolean; plannedStart?: string | null; plannedEnd?: string | null;
    graceMinutes?: number | null; claimFrom?: string | null; claimTo?: string | null } | null;
  punches?: DayPunch[];
  daily?: { status?: string | null; checkIn?: string | null; checkOut?: string | null; delayMinutes?: number; earlyLeaveMinutes?: number; workedMinutes?: number; notes?: string | null } | null;
  absences?: DayDecisionRow[]; deductions?: DayDecisionRow[];
  overtime?: { id: number; minutes: number; kind?: string; status: string } | null;
  vacations?: { id: number; from?: string; to?: string; status: string }[];
  leaves?: { id: number; from?: string; to?: string; status: string }[];
  corrections?: { id: number; status: string; checkIn?: string | null; checkOut?: string | null; excuse?: boolean; reason?: string; timeline?: { step: string; at: string; byId?: string | null }[] }[];
  runs?: { id: number; trigger: string; startedAt: string; finishedAt?: string | null }[];
  dirty?: { waiting: boolean; reason?: string | null; markedAt?: string | null } | null;
}

export type ChecklistList = 'unprocessed' | 'pendingAbsences' | 'pendingDeductions' | 'pendingCorrections' | 'pendingOvertime' | 'unmatchedPunches' | 'ruleHealth' | 'dirtyPairs';
export interface ChecklistPage { data: Record<string, any>[]; page: number; pageSize: number; totalRecords: number }
/** att_review_10: GET api/Attendance/close/checklist. */
export interface CloseChecklist {
  year: number; month: number; closed: boolean; canClose: boolean;
  counts: Record<ChecklistList, number>;
  unprocessed?: ChecklistPage; pendingAbsences?: ChecklistPage; pendingDeductions?: ChecklistPage; pendingCorrections?: ChecklistPage; pendingOvertime?: ChecklistPage;
  unmatchedPunches?: ChecklistPage; ruleHealth?: ChecklistPage; dirtyPairs?: ChecklistPage;
}

export type PayrollAttendanceCheck = 'Off' | 'Warn' | 'Refuse';

/** att_review_04: the attendance concern (GET / PUT api/Attendance/settings/attendance); a PUT sends only the fields the screen owns. */
export interface AttendanceSettings {
  correctionsNeedApproval: boolean;
  payrollAttendanceCheck: PayrollAttendanceCheck;
  /** MOB-00-T02: phones per employee for the mobile app (1 to 5). */
  maxEmployeeDevices?: number;
  /** MOB-10: days back an employee may ask for a correction from the app (0: any open day). */
  correctionRequestDays?: number;
  /** MOB-10: app corrections per employee per month (0: no cap; HR's own corrections are never capped). */
  correctionsPerMonth?: number;
  rejectMockLocations?: boolean;
  rejectRootedDevices?: boolean;
  /** Hours an offline punch stays acceptable after it was taken. */
  offlinePunchMaxHours?: number;
  /** Days the punch selfies are kept. */
  selfieRetentionDays?: number;
}

/** att_review_04: the roster concern (GET / PUT api/Attendance/settings/roster). `clearRamadan` clears both Ramadan dates. */
/** att_review_08: one Ramadan period (at most 31 days, inside its year); dates are yyyy-MM-dd. */
export interface RamadanPeriod { year: number; from: string; to: string; }

export interface RosterSettings {
  ramadanFrom?: string | null;
  ramadanTo?: string | null;
  shiftSwapsPerMonth?: number;
  maxConsecutiveWorkDays?: number;
}

/** att_review_04: the HR-general concern, owned by Company Configuration (0807). */
export interface HrGeneralSettings {
  /** HR-09: vacation days count working days only. */
  vacationCountsWorkingDays?: boolean;
  /** HR-09-T02: encashed vacation days are paid at (basic + fixed allowances) / 30. */
  encashmentIncludesAllowances?: boolean;
  /** HR-03-T05: days before a trial period ends to remind HR and the manager (0: none). */
  probationReminderDays?: number;
  /** JobOpp_review_04: working hours per day a hire from recruitment starts with (1–24; default 8). */
  defaultWorkingHoursPerDay?: number;
  /** JobOpp_review_05 (D5): an offer needs a Pass interview (default on). */
  interviewRequiredBeforeOffer?: boolean;
  /** JobOpp_review_05: an organizer / panel member added after scheduling cannot decide alone (default on). */
  interviewDecisionRequiresPanel?: boolean;
  /** JobOpp_review_07 (D2): the public application form asks for the marital status (default off). */
  askMaritalStatusOnApplication?: boolean;
  /** JobOpp_review_12: sending an offer needs approval first (VT-01 chain "JobOffer"; default off). */
  offerApprovalRequired?: boolean;
  /** JobOpp_review_09: publishing a vacancy needs approval first (VT-01 chain "JobOpportunity"; default off). */
  requisitionApproval?: boolean;
  /** JobOpp_review_09: a knockout answer rejects the application at once (default off: only flagged). */
  autoRejectOnKnockout?: boolean;
}

/** att_review_04: the letters concern (MOB-11), owned by Company Configuration (0807). */
export interface LetterSettings {
  /** A letter the employee asks for is issued without HR's approval. */
  letterAutoApprove?: boolean;
  /** Days an issued letter stays valid (0: never expires). */
  letterExpiryDays?: number;
}

/** sal_review_01 (D1, D2): the payroll concern (GET / PUT api/Attendance/settings/payroll). Start day is 1-28. */
export interface PayrollSettings {
  payPeriodStartDay: number;
  dailyRateBasis: 'Thirty' | 'CalendarDays';
  /** sal_review_05: company override of the deduction cap (percent of the wage); null/absent = the country value. */
  maxDeductionSharePercent?: number | null;
  /** Read-only: the country pack's cap. */
  countryMaxDeductionSharePercent?: number | null;
  /** PUT only: return to the country value. */
  clearMaxDeductionSharePercent?: boolean;
  /** sal_review_04 / 08-T04 (D8): the user who calculates payroll or prepares a settlement cannot approve it (default on). */
  payrollSegregationOfDuties?: boolean;
  /** sal_review_10: the company bank account the salary file is debited from by default (null = the API's default account). */
  salaryBankId?: number | null;
  /** sal_review_10: the payslip PDF shows both languages. */
  payslipBilingual?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AttendanceOperationsService {
  constructor(private http: HttpService) {}

  /** xlsx/csv with a header row: AttendanceNumber (or EmployeeId) and Timestamp (or Date + Time). */
  importPunches(file: File, machineId?: number | null): Observable<{ message: string; data: PunchImportReport }> {
    const form = new FormData();
    form.append('file', file);
    if (machineId) form.append('machineId', String(machineId));
    return this.http.postFormData('Attendance/import', form);
  }

  devices(): Observable<{ data: AttendanceDevice[]; adapters: string[] }> {
    return this.http.getData('Attendance/devices');
  }

  issueKey(deviceId: number): Observable<{ message: string; data: IssuedDeviceKey }> {
    return this.http.postData(`Attendance/devices/${deviceId}/key`, {});
  }

  revokeKey(deviceId: number): Observable<{ message: string }> {
    return this.http.deleteData(`Attendance/devices/${deviceId}/key`);
  }

  /** HR-08 / att_review_12: registers the device's serial number for ZKTeco ADMS push (empty: cleared). Sent as `serialNumber` on PUT devices/{id}/mode (no mode: only the serial changes). */
  setSerial(deviceId: number, serialNumber: string | null): Observable<{ message: string }> {
    return this.http.putData(`Attendance/devices/${deviceId}/mode`, { serialNumber: serialNumber ?? '' });
  }

  setPull(deviceId: number, pullAdapter: string | null): Observable<{ message: string }> {
    return this.http.putData(`Attendance/devices/${deviceId}/pull`, { pullAdapter });
  }

  pullNow(deviceId: number): Observable<{ message: string; data: PunchImportReport }> {
    return this.http.postData(`Attendance/devices/${deviceId}/pull`, {});
  }

  /** ATT-02: how the device sends punches; DevicePush returns the device's server path once. */
  setMode(deviceId: number, mode: Exclude<DeviceMode, 'LegacyPush'>, options: { agentId?: number | null; pullAdapter?: string | null; syncUsers?: boolean } = {}):
    Observable<{ message: string; data: DevicePushSetup | null }> {
    return this.http.putData(`Attendance/devices/${deviceId}/mode`, { mode, ...options });
  }

  setAllowedCidrs(deviceId: number, allowedPushCidrs: string): Observable<{ message: string; data: { allowedPushCidrs: string | null } }> {
    return this.http.putData(`Attendance/devices/${deviceId}/allowed-cidrs`, { allowedPushCidrs });
  }

  admsLog(deviceId: number): Observable<{ data: AdmsRequest[] }> {
    return this.http.getData(`Attendance/devices/${deviceId}/adms-log`);
  }

  deviceCommands(deviceId: number): Observable<{ data: DeviceCommandRow[]; commandTypes: string[] }> {
    return this.http.getData(`Attendance/devices/${deviceId}/commands`);
  }

  queueDeviceCommand(deviceId: number, type: string): Observable<{ message: string; data: { id: number } }> {
    return this.http.postData(`Attendance/devices/${deviceId}/commands`, { type });
  }

  corrections(filter: { status?: CorrectionStatus | ''; employeeId?: number | null; from?: string; to?: string; page?: number; pageSize?: number }):
    Observable<{ data: AttendanceCorrection[]; totalRecords: number; totalPages: number; page: number }> {
    const q = new URLSearchParams();
    if (filter.status) q.set('status', filter.status);
    if (filter.employeeId) q.set('employeeId', String(filter.employeeId));
    if (filter.from) q.set('from', filter.from);
    if (filter.to) q.set('to', filter.to);
    q.set('page', String(filter.page ?? 1));
    q.set('pageSize', String(filter.pageSize ?? 10));
    return this.http.getData(`Attendance/corrections?${q.toString()}`);
  }

  /** Times as HH:mm; the reason is required; the attachment is optional. */
  createCorrection(body: { employeeId: number; date: string; checkIn?: string | null; checkOut?: string | null; excuse: boolean; reason: string },
    attachment?: File | null): Observable<{ message: string; data: AttendanceCorrection }> {
    const form = new FormData();
    form.append('employeeId', String(body.employeeId));
    form.append('date', body.date);
    if (body.checkIn) form.append('checkIn', body.checkIn);
    if (body.checkOut) form.append('checkOut', body.checkOut);
    form.append('excuse', String(body.excuse));
    form.append('reason', body.reason);
    if (attachment) form.append('attachment', attachment);
    return this.http.postFormData('Attendance/corrections', form);
  }

  decide(id: number, approve: boolean, note?: string): Observable<{ message: string }> {
    return this.http.postData(`Attendance/corrections/${id}/decision`, { approve, note });
  }

  closeStatus(year: number, month: number): Observable<{ data: MonthCloseStatus }> {
    return this.http.getData(`Attendance/close/status?year=${year}&month=${month}`);
  }

  /** att_review_10: the employee day (planned, punches, daily row, decisions, corrections, runs). */
  day(employeeId: number, date: string): Observable<{ data: DayView }> {
    return this.http.getData(`Attendance/day?employeeId=${employeeId}&date=${date}`);
  }

  reprocessDay(employeeId: number, date: string): Observable<{ message?: string; data: DayView }> {
    return this.http.postData('Attendance/day/reprocess', { employeeId, date });
  }

  closeChecklist(year: number, month: number, page = 1, pageSize = 20): Observable<{ data: CloseChecklist }> {
    return this.http.getData(`Attendance/close/checklist?year=${year}&month=${month}&page=${page}&pageSize=${pageSize}`);
  }

  processGaps(year: number, month: number): Observable<{ message?: string; data?: { processed: number } }> {
    return this.http.postData('Attendance/close/process-gaps', { year, month });
  }

  acknowledgeUnmatched(id: number, note: string): Observable<{ message?: string }> {
    return this.http.postData(`Attendance/close/unmatched/${id}/acknowledge`, { note });
  }

  close(year: number, month: number): Observable<{ message: string }> {
    return this.http.postData('Attendance/close', { year, month });
  }

  /** att_review_02: a 409 (approved / paid salaries cover the month) keeps its body as an ApiError (data.salaries); `force` reopens anyway. */
  reopen(year: number, month: number, reason: string, force = false): Observable<{ message: string }> {
    return this.http.postCoded('Attendance/reopen', force ? { year, month, reason, force: true } : { year, month, reason });
  }

  attendanceSettings(): Observable<{ data: AttendanceSettings }> {
    return this.http.getData('Attendance/settings/attendance');
  }

  saveAttendanceSettings(body: Partial<AttendanceSettings>): Observable<{ message: string }> {
    return this.http.putData('Attendance/settings/attendance', body);
  }

  rosterSettings(): Observable<{ data: RosterSettings }> {
    return this.http.getData('Attendance/settings/roster');
  }

  /** att_review_08: the Ramadan period of every year (newest first by the screen). */
  ramadanPeriods(): Observable<{ data: RamadanPeriod[] }> {
    return this.http.getData('Attendance/settings/ramadan-periods');
  }

  saveRamadanPeriod(body: RamadanPeriod): Observable<{ message: string }> {
    return this.http.putCoded('Attendance/settings/ramadan-periods', body);
  }

  saveRosterSettings(body: Partial<RosterSettings> & { clearRamadan?: boolean }): Observable<{ message: string }> {
    return this.http.putData('Attendance/settings/roster', body);
  }

  hrGeneralSettings(): Observable<{ data: HrGeneralSettings }> {
    return this.http.getData('Attendance/settings/hr-general');
  }

  saveHrGeneralSettings(body: HrGeneralSettings): Observable<{ message: string }> {
    return this.http.putData('Attendance/settings/hr-general', body);
  }

  payrollSettings(): Observable<{ data: PayrollSettings }> {
    return this.http.getData('Attendance/settings/payroll');
  }

  savePayrollSettings(body: PayrollSettings): Observable<{ message: string }> {
    return this.http.putData('Attendance/settings/payroll', body);
  }

  lettersSettings(): Observable<{ data: LetterSettings }> {
    return this.http.getData('Attendance/settings/letters');
  }

  saveLettersSettings(body: LetterSettings): Observable<{ message: string }> {
    return this.http.putData('Attendance/settings/letters', body);
  }
}
